import React from 'react';

import { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import underline24Mobile from 'stile-shared/static/underline-24-mobile.svg';
import underline24 from 'stile-shared/static/underline-24.svg';
import underline25Mobile from 'stile-shared/static/underline-25-mobile.svg';
import underline25 from 'stile-shared/static/underline-25.svg';
import underline26Mobile from 'stile-shared/static/underline-26-mobile.svg';
import underline26 from 'stile-shared/static/underline-26.svg';
import styled from 'styled-components';

import iconPage from 'stile-shared/assets/icons/icon_page.svg';
import Jules from 'stile-shared/assets/images/responsive-support/avatar-jules.png';
import Leah from 'stile-shared/assets/images/secure-and-private/avatar-leah.png';
import Paul from 'stile-shared/assets/images/secure-and-private/avatar-paul.png';

import { Button } from 'stile-shared/src/components/2024/common/Button';
import { ExploreCurriculum } from 'stile-shared/src/components/2024/common/ExploreCurriculum';
import { Icon } from 'stile-shared/src/components/2024/common/Icon';
// Layout components
import TitleMenu from 'stile-shared/src/components/2024/common/Included/WITBTitleMenu/WITBTitleMenu';
import Top from 'stile-shared/src/components/2024/common/Included/WITBTopSection/WITBTopSection';
import {
  EvidenceSection,
  StyledEvidenceCard,
} from 'stile-shared/src/components/2024/layout/CardSection';
import { Section } from 'stile-shared/src/components/2024/layout/Section';

import { PageLayout } from 'templates/2024/PageLayout';

export { Head } from 'templates/2024/Head';

const SecurityLayout = styled(PageLayout)`
  .underline-24,
  .underline-25,
  .underline-26 {
    display: inline-block;
    position: relative;
    z-index: 1;

    &::before {
      content: '';
      display: block;
      position: absolute;
      background-repeat: no-repeat;
      background-position: top left;
      background-size: cover;
      z-index: -1;
      bottom: -4px;
    }
  }
  .underline-24 {
    &::before {
      background-image: url('${underline24}');
      left: -10px;
      width: 146px;
      height: 13px;
      @media (max-width: 768px) {
        background-image: url('${underline24Mobile}');
        bottom: -3px;
        left: -4px;
        width: 91px;
        height: 9px;
      }
    }
  }
  .underline-25 {
    &::before {
      background-image: url('${underline25}');
      width: 143px;
      height: 13px;
      @media (max-width: 768px) {
        background-image: url('${underline25Mobile}');
        bottom: -3px;
        left: -3px;
        width: 94px;
        height: 9px;
      }
    }
  }
  .underline-26 {
    &::before {
      background-image: url('${underline26}');
      width: 129px;
      height: 15px;
      @media (max-width: 768px) {
        background-image: url('${underline26Mobile}');
        bottom: -4px;
        left: -1px;
        width: 86px;
        height: 10px;
      }
    }
  }
`;

const TitleMenuContainer = styled.div`
  display: flex !important;
  flex-direction: column !important;
  padding-bottom: 80px !important;

  @media (max-width: 768px) {
    padding: 0 25px 0 !important;
    margin: 0 !important;
    > div > div {
      padding-top: calc(32px - 15px) !important;
    }
  }
`;

const FullCastSection = styled(Section)`
  margin: 12px 0 150px !important;
  padding: 0 !important;
  background: transparent;

  @media (max-width: 768px) {
    margin: 0 0 60px !important;
    width: 100vw !important;

    .section {
      width: 100vw !important;
    }
    .content {
      padding-top: 60px !important;
      .grid-layout {
        margin: 0 !important;
      }
    }
  }
`;

const FullCastInner = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column !important;
  align-items: center;
  gap: 32px;
  padding: 60px;
  background: var(--stile-grey-1);
  border-radius: 32px;

  @media (max-width: 768px) {
    padding: 60px 0;
    gap: 32px;
    border-radius: 20px !important;
  }
`;

const FullCastHeader = styled.div`
  width: 100%;
  text-align: center;
  gap: 10px;
  align-items: center;
  display: flex;
  flex-direction: column;
  @media (max-width: 768px) {
    gap: 15px;
  }
`;

const FullCastTitleDesktop = styled.h2`
  font-size: 40px;
  margin: 0 !important;
  display: block;
  @media (max-width: 768px) {
    display: none;
  }
`;

const FullCastTitleMobile = styled.h4`
  margin: 0 !important;
  display: none;
  @media (max-width: 768px) {
    display: block;
  }
`;

const FullCastDescription = styled.p`
  width: 100%;
  max-width: 565px;
  margin: 0 auto;
  .buttons {
    display: flex;
    gap: 10px;
    justify-content: center;
    margin-top: 40px;
    a {
      height: 38px;
    }
  }
  @media (max-width: 768px) {
    width: 90%;
    max-width: none;
    .buttons {
      a {
        width: 100%;
        justify-content: center;
      }
      flex-direction: column;
      margin-top: 32px;
    }
  }
`;

interface FullCastImagesProps {
  height?: number;
}

const FullCastImages = styled.div<FullCastImagesProps>`
  display: flex;
  justify-content: center;
  --base-height: ${({ height = 126 }) => height}px;
  height: var(--base-height);
  position: relative;
  align-items: baseline;
  max-width: unset !important;

  @media (max-width: 768px) {
    --base-height: calc(126px * 0.7);
  }
`;

const ProfileImage = styled.img<{ distance?: number }>`
  position: absolute;
  --base-height: ${({ height = 126 }) => height}px;
  border-radius: 50%;
  border: 3.38px solid white;
  aspect-ratio: 1;
  object-fit: cover;
  object-position: top;
  bottom: 0;
  max-width: unset !important;

  transform: ${({ distance = 0 }) =>
    `translateX(calc(${distance} * calc(var(--base-height) * 0.6)))`};

  @media (max-width: 768px) {
    --base-height: calc(126px * 0.7);
  }
`;

const MediumImage = styled(ProfileImage)`
  height: calc(var(--base-height) / 7 * 6); // medium images are 1/7th less than the large
  z-index: 6;
`;

const LargeImage = styled(ProfileImage)`
  height: var(--base-height) !important;
  z-index: 7;
`;

const StyledBehindStileXCard = styled<typeof StyledEvidenceCard>(StyledEvidenceCard)`
  .--content {
    p {
      margin: 0;
    }
  }
  h4 {
    padding-top: 10px;
    padding-bottom: 0 !important;
  }
`;

const ScienceBehindStileXSection = styled<typeof EvidenceSection>(EvidenceSection)`
  .content {
    padding-top: 150px !important;
    @media (max-width: 768px) {
      padding-top: 60px !important;
      padding-bottom: 20px !important;
    }
  }
  h2 {
    grid-column: 4 / span 6 !important;
    @media (max-width: 768px) {
      grid-column: 1 / span 12 !important;

    }
  }
  .--subheader {
    margin-bottom: 100px;
    @media (max-width: 768px) {
      margin-bottom: 40px;
    }
  }
  .examples {
    grid-column: 1 / span 12;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(2, 1fr);
    height: 536px;
    padding: 20px 0 0 0;
    gap: 80px 28px;
    @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
      gap: 32px 12px;
      height: 712px;
    }

  .exampleCard {
    display: flex;
    max-width: 368px;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    h6 {
      margin: 0;
    }
    @media (max-width: 768px) {
      width: 164px;
      .gatsby-image-wrapper {
          width: 100px;
      }
    }
  }
`;
const StyledSection = styled(Section)`
  .content {
    padding-top: 0px !important;
  }
  .left {
    h2 {
      text-align: left !important;
      margin-bottom: 20px !important;
    }
    grid-column: 1 / span 5;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .right {
    grid-column: 7 / span 6;
    .gatsby-image-wrapper {
      border-radius: 32px;
      box-shadow: 0px 43px 26px 0px rgba(0, 0, 0, 0.04), 0px 19px 19px 0px rgba(0, 0, 0, 0.06),
        0px 5px 11px 0px rgba(0, 0, 0, 0.07);
      @media (max-width: 768px) {
        border-radius: 20px;
      }
    }
  }
  margin-bottom: 150px !important;
  @media (max-width: 768px) {
    margin-bottom: 60px !important;
    .left,
    .right {
      grid-column: 1 / span 12;
    }
    .left {
      grid-row: 2;
    }
    .right {
      margin-bottom: 30px;
      grid-row: 1;
    }
  }
`;
const PurposeDrivenSection = styled<typeof ScienceBehindStileXSection>(ScienceBehindStileXSection)`
  .content {
    padding-top: 0 !important;
    padding-bottom: 100px !important;
    @media (max-width: 768px) {
      padding-bottom: 10px !important;
    }
  }
  h2 {
    margin-bottom: 80px !important;
    @media (max-width: 768px) {
      margin-bottom: 40px !important;
    }
  }
`;
const PurposeDrivenContent = styled.div`
  grid-column: 4 / span 6;
  display: flex;
  flex-direction: column;
  gap: 60px;
  margin-bottom: 50px;
  p {
    margin-bottom: 12px;
  }
  h4 {
    margin: 0 0 20px;
  }
  a {
    margin-top: 20px;
    height: 38px;
  }
  ol {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-inline-start: 23px;
  }
  @media (max-width: 768px) {
    gap: 40px;
    grid-column: 1 / span 12;
    a {
      margin-top: 20px;
    }
  }
`;
function SecurityPage(props: PageProps) {
  const exploreCards = [
    {
      title: 'Guarantees and Principles',
      description: 'Some talk the talk, we walk the walk. Discover our non-negotiables.',
      url: '/who-we-are/our-principles',
    },
    {
      title: 'Technology',
      description:
        'From customising lessons to tracking student performance, you`re always in control.',
      url: '/what-is-stile/technology-for-teaching/',
    },
    {
      title: 'Responsive Support',
      description:
        'From phone to email to help desk, learn how we support you every step of the way.',
      url: '/what-is-stile/responsive-support/',
    },
  ];
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();

    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  return (
    <SecurityLayout {...props}>
      <TitleMenuContainer className="content">
        <TitleMenu>
          <Top
            title="Secure and private, always."
            description="Driven by a team of scientists, teachers, and engineers, our commitment to excellence shapes everything we do—from meticulously crafted resources to the robust engineering that powers the most stable, secure, and effective platform in science education."
          />
        </TitleMenu>
      </TitleMenuContainer>
      <StyledSection>
        <div className="left">
          <h2>A decade of privacy-first education</h2>
          <p>
            Choosing the right tech platform is a big decision—one where you need absolute
            confidence that your data is in safe hands. At Stile, privacy has been our core,
            non-negotiable commitment for over a decade. In all that time, we’ve never had a data
            breach.
            <br />
            <br />
            It’s one of the many reasons our platform is trusted by over 1,000 schools. And
            counting.
          </p>
        </div>
        <div className="right">
          <StaticImage
            src="../../../../stile-shared/assets/images/secure-and-private/privacy_first.jpg"
            alt="Privacy first education"
          />
        </div>
      </StyledSection>
      <PurposeDrivenSection
        backgroundColor="--white"
        headingContent="Purpose-driven privacy"
        textColor="--stile-grey-8"
      >
        <PurposeDrivenContent>
          <div>
            <h4>
              Stile is the product; <span className="underline-25">not you</span>
            </h4>
            <p>
              Let’s be clear: Stile is the product; you are not. We’re here for one reason only—to
              provide the best possible education for every student. That means:
            </p>
            <ol>
              <li>We do not claim any ownership over your data: it’s yours.</li>
              <li>
                We do not and will never use your data to advertise or sell/give your data to third
                parties.
              </li>
              <li>
                We only collect the minimum amount of data needed to provide our ever-improving
                educational services.
              </li>
              <li>We lead the industry in keeping the data that we do collect secure.</li>
            </ol>
            <Button
              className="iconButton"
              newTab={true}
              href="https://stileapp.com/go/security-guide"
              label={
                <>
                  <Icon src={iconPage} iconSize="20px" style={{ marginRight: 6 }} />
                  <span>See how here</span>
                </>
              }
            />
          </div>
          <div>
            <h4>
              Private by <span className="underline-26">default</span>
            </h4>
            <p>
              We understand the trust you place in us, and we know the information we protect is
              personal.
            </p>
            <Button label="View our full privacy policy" href="/other/privacy/" newTab={true} />
          </div>
          <div>
            <h4>
              <span className="underline-24">Always</span> secure
            </h4>
            <p>
              Our team is always on top of the latest global security practices, and takes pride in
              pushing the Education sector forward. Through our partnership with{' '}
              <strong>Amazon Web Services (AWS)</strong>, a global leader in secure infrastructure,
              we’ve remained breach-free (and proud), providing the highest level of protection for
              our students and schools.
            </p>
            <p>
              Our systems are rigorously tested — for more than 30 hours — every time we make a
              change. If that’s not enough, we then hire professional hackers to try to find a way
              through our defences (and our engineers delight in frustrating them year on year!)
            </p>
            <p>
              It doesn’t stop there though: we believe so strongly in our security, that{' '}
              <strong>we’ll give you up to $50,000 if you can find a way to break it!</strong>
            </p>
          </div>
        </PurposeDrivenContent>
      </PurposeDrivenSection>
      <ScienceBehindStileXSection
        backgroundColor="--stile-grey-1"
        headingContent="How we measure up"
        textColor="--stile-grey-8"
      >
        <div className="--subheader">
          We actively seek external reviews and feedback to help us continually improve. Here’s how
          we do it.
        </div>
        <StyledBehindStileXCard
          title="Safer Technology for Schools"
          copy={
            <div>
              <p>
                The ST4S program sets a national benchmark for privacy, security, and risk in school
                technology. Stile proudly holds an ST4S Badge with top safety ratings, maintained
                through annual readiness checks.
              </p>
            </div>
          }
          image={
            isMobile ? (
              <StaticImage
                src="../../../../stile-shared/assets/images/secure-and-private/safer_technology_for_schools_mobile.png"
                alt="Safer Technology for Schools"
              />
            ) : (
              <StaticImage
                src="../../../../stile-shared/assets/images/secure-and-private/safer_technology_for_schools.png"
                alt="Safer Technology for Schools"
              />
            )
          }
        />
        <StyledBehindStileXCard
          title="SOC2 Certified"
          copy={
            <div>
              <p>
                Stile meets the strictest industry standards with SOC2 certification. Regular audits
                ensure that our systems are secure, reliable, and built to protect your data.
              </p>
            </div>
          }
          image={
            isMobile ? (
              <StaticImage
                src="../../../../stile-shared/assets/images/secure-and-private/soc2_mobile.png"
                alt="SOC2 Certified"
              />
            ) : (
              <StaticImage
                src="../../../../stile-shared/assets/images/secure-and-private/soc2.png"
                alt="SOC2 Certified"
              />
            )
          }
        />
        <StyledBehindStileXCard
          title="Human Rights Watch"
          copy={
            <div>
              <p>
                Stile’s dedication to privacy and security was highlighted in a 2022 Human Rights
                Watch report, naming us one of just 12 (out of 124) education technology companies
                managing student data responsibly.
              </p>
            </div>
          }
          image={
            isMobile ? (
              <StaticImage
                src="../../../../stile-shared/assets/images/secure-and-private/human_rights_watch_mobile.png"
                alt="Human Rights Watch"
              />
            ) : (
              <StaticImage
                src="../../../../stile-shared/assets/images/secure-and-private/human_rights_watch.png"
                alt="Human Rights Watch"
              />
            )
          }
        />
      </ScienceBehindStileXSection>

      <FullCastSection>
        <FullCastInner>
          <FullCastImages style={{ maxWidth: 'unset' }}>
            {/* <SmallImage src={Lavinia} alt="Small profile" distance={-2} /> */}
            <MediumImage src={Paul} alt="Medium profile" distance={-1} />
            <LargeImage src={Jules} alt="Large profile" distance={0} />
            <MediumImage src={Leah} alt="Medium profile" distance={1} />
            {/* <SmallImage src={Marcus} alt="Small profile" distance={2} /> */}
          </FullCastImages>

          <FullCastHeader>
            <FullCastTitleMobile>Need help? We’re here</FullCastTitleMobile>
            <FullCastTitleDesktop>Need help? We’re here</FullCastTitleDesktop>
            <FullCastDescription>
              When it comes to your data, only our select, highly trained team has access—and only
              when you’ve given us explicit permission. We rigorously control access, log every
              action, and conduct regular audits to ensure your information is protected at all
              times.
              <div className="buttons">
                <Button
                  className="iconButton"
                  newTab={true}
                  href="https://stileapp.com/go/security-guide"
                  label={
                    <>
                      <Icon src={iconPage} iconSize="20px" style={{ marginRight: 6 }} />
                      <span>Read Security & Privacy Guide</span>
                    </>
                  }
                />
                <Button label="Reach out" href="/who-we-are/contact/" newTab={true} />
              </div>
            </FullCastDescription>
          </FullCastHeader>
        </FullCastInner>
      </FullCastSection>
      <ExploreCurriculum
        title="Explore our core science curriculum"
        exploreCardsData={exploreCards}
        image={
          <StaticImage
            src="../../../../stile-shared/assets/images/responsive-support/book-with-grad-cap.svg"
            alt="A book with a graduation cap"
          />
        }
      />
    </SecurityLayout>
  );
}

export default SecurityPage;
